import type { AccountSectionId, IAccountSection } from './types';

export type AccountGuideId = 'wallet' | 'cashout' | 'payment' | 'authorized';

export interface IAccountGuideContext {
  sections: IAccountSection[];
  hasWallet: boolean;
}

export interface IAccountGuide {
  id: AccountGuideId;
  /** Number of `my.account.hub.guide.<id>.stepN` keys. */
  steps: number;
  readMinutes: number;
  /** Section the guide's action button opens, when the member can see it. */
  actionSection?: AccountSectionId;
  isAvailable: (context: IAccountGuideContext) => boolean;
}

const hasSection = (context: IAccountGuideContext, id: AccountSectionId) =>
  context.sections.some(section => section.id === id);

/**
 * Help guides shown on Overview. Copy lives in i18n (`my.account.hub.guide.*`) as plain text, never
 * HTML. A guide only shows when the feature it explains is available to the member.
 */
export const ACCOUNT_GUIDES: IAccountGuide[] = [
  {
    id: 'wallet',
    steps: 3,
    readMinutes: 1,
    isAvailable: context => context.hasWallet,
  },
  {
    id: 'cashout',
    steps: 4,
    readMinutes: 2,
    // Turns on with the cash-out section (TRAV23-4671, wallet PR). Until then there is nothing to explain.
    isAvailable: () => false,
  },
  {
    id: 'payment',
    steps: 4,
    readMinutes: 1,
    actionSection: 'payment',
    isAvailable: context => hasSection(context, 'payment'),
  },
  {
    id: 'authorized',
    steps: 4,
    readMinutes: 1,
    actionSection: 'authorized',
    isAvailable: context => hasSection(context, 'authorized'),
  },
];

export const getAccountGuides = (context: IAccountGuideContext): IAccountGuide[] =>
  ACCOUNT_GUIDES.filter(guide => guide.isAvailable(context));
